import {
  Checkbox,
  Grid,
  makeStyles,
  MenuItem,
  MenuList,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { cloneDeep } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';

import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { publicaColors } from '~/src/dash/utils/colors';

const useStyles = makeStyles({
  customWidth: {
    '& div': {
      width: 450,
    },
  },
  textField: {
    '& div': {
      backgroundColor: '#e8edf4',
      width: 430,
      margin: 10,
    },
    '& input': {
      padding: 10,
    },
  },
  menuItem: {
    padding: 8,
    maxHeight: 40,
  },
  menuOpen: {
    visibility: 'visible',
  },
  menuClosed: {
    visibility: 'hidden',
  },
});

interface IProps {
  menuTitle: string;
  stateKey: string;
  list: IMenuList;
  initialDisplayLimit?: number;
  menuAcceptClickCallbacks: Array<() => void>;
  onChange: (keys: string[], values: any[], setStateCallbacks: Array<() => void>) => void;
  onChangeFuncComponent?: (newMenuList: IMenuList) => void;
  handleMenuAcceptClick: (menuAcceptClickCallbacks: Array<() => void>) => void;
}

export const SearchDropdownSingle = (props: IProps) => {
  const wrapperRefOpen = useRef<HTMLDivElement>(null);
  const wrapperRefClose = useRef<HTMLDivElement>(null);
  const classes = useStyles();
  const { menuTitle, list, handleMenuAcceptClick, onChange, onChangeFuncComponent, stateKey } =
    props;
  const menuAcceptClickCallbacks = props.menuAcceptClickCallbacks
    ? props.menuAcceptClickCallbacks
    : [];
  const initialDisplayLimit = props.initialDisplayLimit || 300;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState<string>('');
  const [initialSingleSelect, setInitialSingleSelect] = useState<string>('');
  const [bottomIndexDisplayed, setBottomIndexDisplayed] = useState<number>(initialDisplayLimit);
  const [prevList, setPrevList] = useState<IMenuList>(props.list);

  if (Object.keys(props.list).length !== 0 && Object.keys(prevList).length === 0) {
    setPrevList(props.list);
  }
  let numSelected = 0;
  let firstSelection = '';

  // Adds/removes selected from item in hash then sets state in parent component
  const handleItemClick = (title: string): void => {
    const newList = cloneDeep(list);
    for (const key of Object.keys(newList)) {
      if (key === title) {
        newList[key].selected = true;
      } else {
        newList[key].selected = false;
      }
    }

    if (onChangeFuncComponent) {
      onChangeFuncComponent(newList);
      setIsOpen(false);
    } else {
      onChange([stateKey], [newList], [handleMenuAccept]);
    }
  };

  // handles changes to filter and sets state in parent
  const handleTextInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchInput(e.currentTarget.value);
    setBottomIndexDisplayed(initialDisplayLimit);
  };

  // checks to see if list is near bottom, if it is, loads more menu items
  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    const bottom =
      e.currentTarget.scrollHeight - e.currentTarget.scrollTop === e.currentTarget.clientHeight;
    if (bottom) {
      setBottomIndexDisplayed(bottomIndexDisplayed + initialDisplayLimit);
    }
  };

  // closes component when clicked outside, opens component when clicked on
  function handleClickOutside(this: Document, e: MouseEvent): void {
    if (wrapperRefClose.current !== null && wrapperRefClose.current.contains(e.target as Node)) {
      handleMenuOpen();
    } else if (
      wrapperRefOpen.current !== null &&
      !wrapperRefOpen.current.contains(e.target as Node)
    ) {
      setIsOpen(false);
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside, false);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, false);
    };
  }, [initialSingleSelect]);

  const filteredList = Object.values(list).filter((item: IListItem): boolean => {
    if (item.title === 'Select All') {
      return true;
    }
    if (item.selected) {
      if (firstSelection === '') {
        firstSelection = item.title;
        if (initialSingleSelect === '') {
          setInitialSingleSelect(item.title);
        }
      }
      numSelected++;
    }
    return item.title.toLowerCase().indexOf(searchInput.toLowerCase()) > -1;
  });

  const handleMenuOpen = (): void => {
    setIsOpen(true);
    setInitialSingleSelect(firstSelection);
  };

  const handleMenuAccept = (): void => {
    setIsOpen(false);
    handleMenuAcceptClick(menuAcceptClickCallbacks);
  };

  // displays the name of the first selection if we only have 1 item vs the number
  const menuClosedDisplay =
    numSelected === 1 ? firstSelection : `${numSelected} ${menuTitle} Selected`;
  return (
    <Grid style={{ position: 'relative' }}>
      <Grid
        container
        style={{
          border: '1px solid #e3e2e3',
          borderRadius: 4,
          padding: '4px 12px',
          maxWidth: 450,
        }}
        justify='space-between'
        direction='row'
        ref={wrapperRefClose}
      >
        <Grid
          item
          lg={10}
          md={10}
          sm={10}
          xs={10}
          style={{
            fontSize: 14,
            overflow: 'hidden',
          }}
        >
          {numSelected === 0 ? `Select ${menuTitle}` : menuClosedDisplay}
        </Grid>
        <Grid item lg={2} md={2} sm={2} xs={2}>
          <Grid container justify='flex-end' alignItems='center'>
            <IconV2 name='caret' color={publicaColors.veryDarkGray} />
          </Grid>
        </Grid>
      </Grid>
      {isOpen && (
        <Paper style={{ position: 'absolute', left: 0, width: 450, zIndex: 11 }}>
          <Grid ref={wrapperRefOpen} className={isOpen ? classes.menuOpen : classes.menuClosed}>
            <TextField
              onChange={handleTextInput}
              value={searchInput}
              variant='filled'
              className={classes.textField}
              placeholder={`Search ${menuTitle}`}
              InputProps={{ disableUnderline: true }}
              inputRef={(input) => input && input.focus()}
            />
            <MenuList onScroll={handleScroll} style={{ maxHeight: 400, overflow: 'scroll' }}>
              {filteredList.slice(0, bottomIndexDisplayed).map((item: IListItem) => {
                if (item === undefined) {
                  return;
                }
                const { title, selected } = item;
                return (
                  <Tooltip
                    title={item.description !== undefined ? item.description : title}
                    key={item.title}
                  >
                    <MenuItem className={classes.menuItem} onClick={() => handleItemClick(title)}>
                      <Checkbox checked={selected} />
                      <Typography>
                        {title === 'Select All'
                          ? `Select All (${filteredList.length - 1} items)`
                          : title}
                      </Typography>
                    </MenuItem>
                  </Tooltip>
                );
              })}
            </MenuList>
          </Grid>
        </Paper>
      )}
    </Grid>
  );
};
